import { Box, Stack } from "@mui/material";
import { Flex, SearchField } from "@zfp-akademie-a-s/zfp-design";
import { useSearchClients } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { SearchedClient, SearchedLocalClient } from "Infrastructure/ApiZfp/Schema";
import { useOnClickOutside } from "Infrastructure/Hooks/useOnClickOutside";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { debounce } from "lodash-es";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { ClientsSearchResults } from "./ClientsSearchResults";

type Props = {
  onSelect: (client: SearchedClient) => void;
  onSelectLocal?: (client: SearchedLocalClient) => void;
  includeLocalClients?: boolean;
  showAnalysisStatus?: boolean;
};

export const ClientsSearch = ({ onSelect, onSelectLocal, includeLocalClients = false, showAnalysisStatus = false }: Props) => {
  const [existingClients, setExistingClients] = useState<SearchedClient[]>([]);
  const [localClients, setLocalClients] = useState<SearchedLocalClient[]>([]);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useResource();

  const { mutate, isPending } = useSearchClients({
    mutation: {
      onSuccess: (data) => {
        setExistingClients(data.existingClients);
        setLocalClients(includeLocalClients ? data.localClients : []);
        setShowResults(true);
      },
      onError: (_) => {
        toast.error(t(Resources.Clients.SearchError));
      },
    },
  });

  const debouncedSearch = useCallback(
    debounce((phrase: string) => {
      mutate({ data: { phrase } });
    }, 300),
    [],
  );

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    if (term.length >= 2) {
      debouncedSearch(term);
    }
  };

  const handleFocus = () => {
    if (existingClients.length > 0 || localClients.length > 0) {
      setShowResults(true);
    }
  };

  const selectClient = (client: SearchedClient) => {
    onSelect(client);
    setShowResults(false);
  };

  const selectLocalClient = (client: SearchedLocalClient) => {
    onSelectLocal?.(client);
    setShowResults(false);
  };

  const ref = useRef<HTMLDivElement | null>(null);
  const handleOutsideClick = useCallback(() => {
    setShowResults(false);
  }, []);

  useOnClickOutside(ref, handleOutsideClick);

  return (
    <Flex width={"100%"} justifyContent="center">
      <Stack m={2} position={"relative"} spacing={1} sx={{ width: "600px" }} borderRadius={2}>
        <Box ref={ref}>
          <SearchField
            value={searchTerm}
            onChange={handleSearch}
            onFocus={handleFocus}
            placeholder={t(Resources.Clients.SearchPlaceholder)}
            ariaLabel={t(Resources.Clients.SearchPlaceholderShort)}
            clearLabel={t(Resources.Clients.ClearSearch)}
            fullWidth
            sx={{ mb: 1 }}
          />
          {(isPending || showResults) && (
            <ClientsSearchResults
              isLoading={isPending}
              existingClients={existingClients}
              localClients={includeLocalClients ? localClients : undefined}
              onSelect={selectClient}
              onSelectLocal={selectLocalClient}
              showAnalysisStatus={showAnalysisStatus}
            />
          )}
        </Box>
      </Stack>
    </Flex>
  );
};
